// Kiosk-local settings (DESIGN.md section 9.1 — Settings panel, 2026-08-31 round-8 feedback item
// 23). "Voice commands (mic)" gates whether the APP HANDLES STT/voice events — the speech-to-text
// service itself runs device-side (kmb-stt, apps/side/pingpong/kmb-stt/, not shipped to the real
// kiosk yet — see DESIGN.md's HelpOverlay note) and is out of this app's reach; flipping this off
// just makes the kiosk ignore whatever voice events arrive, the same way a screen with no
// contextual meaning for a badge scan ignores that. "Semantic fallback" (round-24, 2026-08-31)
// separately gates the in-browser embedding-model Tier-2 matcher (src/semanticIntent.ts,
// DESIGN.md §11) on top of the closed-grammar path. Both persisted to localStorage (per-device,
// not synced anywhere) so they survive a reload — device preferences, not app/match state, so
// they deliberately do NOT go through the backend or the SSE stream.
import { reactive, watch } from 'vue'

const STORAGE_KEY = 'pingpong.settings.v1'

interface Settings {
  voiceCommandsEnabled: boolean
  // Round-24 (2026-08-31): Tier-2 semantic intent fallback (DESIGN.md §11 "Semantic voice
  // matching", src/semanticIntent.ts) — a small in-browser embedding model that maps SIMILAR
  // phrasings ("vamos a jugar otra vez") to the same intents the closed grammar recognizes
  // exactly, for a small navigation/setup-only allowlist (never win_claim/point_claim/score).
  // Default ON but instantly killable, same as voiceCommandsEnabled — flipping this off stops
  // the fallback from ever firing (the model may still be loaded in memory, it's just never
  // consulted) without touching the grammar path at all.
  semanticFallbackEnabled: boolean
  // Round-31 (2026-08-31, DESIGN.md §23) — "Fast mode": trades per-point tracking (taps/voice
  // for every single point) for a quick final-score entry per game, so a match doesn't have to
  // be interrupted point-by-point. ENABLED BY DEFAULT per the user's own framing ("tracking
  // every single point ... is a bit awkward") — this is the expected common case, not an opt-in
  // power feature. Read once, at match creation (api.ts's CreateMatchPayload.fastMode) — it's
  // NOT live-reactive mid-match; flipping it here only affects the NEXT match started.
  fastMode: boolean
}

function loadInitial(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (
        typeof parsed.voiceCommandsEnabled === 'boolean' ||
        typeof parsed.semanticFallbackEnabled === 'boolean' ||
        typeof parsed.fastMode === 'boolean'
      ) {
        return {
          voiceCommandsEnabled: typeof parsed.voiceCommandsEnabled === 'boolean' ? parsed.voiceCommandsEnabled : true,
          semanticFallbackEnabled: typeof parsed.semanticFallbackEnabled === 'boolean' ? parsed.semanticFallbackEnabled : true,
          fastMode: typeof parsed.fastMode === 'boolean' ? parsed.fastMode : true,
        }
      }
    }
  } catch {
    // Corrupt/missing storage — fall through to the default.
  }
  return { voiceCommandsEnabled: true, semanticFallbackEnabled: true, fastMode: true }
}

export const settings = reactive<Settings>(loadInitial())

watch(
  settings,
  (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    } catch {
      // Storage full/unavailable — the toggle still works for this session, just doesn't persist.
    }
  },
  { deep: true },
)
